import { Link } from 'react-router-dom'
import {
  Building2,
  Gauge,
  FileText,
  Mail,
  CreditCard,
  Shield,
  Clock,
  Users,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Zap,
  TrendingUp,
  Calculator
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

const features = [
  {
    icon: Building2,
    title: 'Zarządzanie wspólnotami',
    description: 'Pełna hierarchia: wspólnoty, klatki, lokale. Wszystkie dane w jednym miejscu.',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    icon: Gauge,
    title: 'System liczników',
    description: 'Zarządzaj licznikami wody, gazu i prądu. Automatyczne alerty o anomaliach.',
    gradient: 'from-emerald-500 to-teal-500'
  },
  {
    icon: Calculator,
    title: 'Automatyczne rozliczenia',
    description: 'Kalkulacja czynszu wg osób, powierzchni, liczników lub udziałów.',
    gradient: 'from-violet-500 to-purple-500'
  },
  {
    icon: FileText,
    title: 'Generowanie PDF',
    description: 'Profesjonalne dokumenty rozliczeniowe gotowe do wysyłki.',
    gradient: 'from-orange-500 to-amber-500'
  },
  {
    icon: Mail,
    title: 'Powiadomienia email',
    description: 'Automatyczna wysyłka rozliczeń i przypomnień do mieszkańców.',
    gradient: 'from-pink-500 to-rose-500'
  },
  {
    icon: CreditCard,
    title: 'Import bankowy',
    description: 'Importuj wyciągi bankowe i automatycznie paruj płatności.',
    gradient: 'from-indigo-500 to-blue-500'
  }
]

const benefits = [
  {
    icon: Clock,
    stat: '80%',
    label: 'Mniej czasu',
    description: 'na miesięczne rozliczenia'
  },
  {
    icon: Shield,
    stat: '100%',
    label: 'Dokładność',
    description: 'automatycznych obliczeń'
  },
  {
    icon: Users,
    stat: '122+',
    label: 'Lokali',
    description: 'zarządzanych w systemie'
  },
  {
    icon: TrendingUp,
    stat: '4',
    label: 'Wspólnoty',
    description: 'już korzystają z ZAMEK'
  }
]

const workflowSteps = [
  {
    step: '01',
    title: 'Wprowadź odczyty',
    description: 'Masowe wprowadzanie odczytów liczników dla wszystkich lokali naraz.',
    icon: Gauge
  },
  {
    step: '02',
    title: 'Wygeneruj rozliczenia',
    description: 'System automatycznie obliczy należności na podstawie stawek i zużycia.',
    icon: Calculator
  },
  {
    step: '03',
    title: 'Wyślij do mieszkańców',
    description: 'Jednym kliknięciem wyślij PDF-y emailem do wszystkich właścicieli.',
    icon: Mail
  },
  {
    step: '04',
    title: 'Śledź płatności',
    description: 'Monitoruj wpłaty i zadłużenia w przejrzystym panelu.',
    icon: CreditCard
  }
]

const pricingFeatures = [
  'Nielimitowane wspólnoty i lokale',
  'Pełny system liczników z historią',
  'Automatyczne rozliczenia okresowe',
  'Generator dokumentów PDF',
  'Wysyłka email do mieszkańców',
  'Import wyciągów bankowych',
  'Śledzenie zadłużeń',
  'Raporty i statystyki',
  'Portal dla właścicieli',
  'Wsparcie techniczne'
]

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                ZAMEK
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="font-medium">
                  Zaloguj się
                </Button>
              </Link>
              <a href="mailto:kontakt@ai-dla-firmy.pl?subject=ZAMEK%20-%20Prośba%20o%20demo">
                <Button className="font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all">
                  Umów demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-80 h-80 bg-gradient-to-tr from-emerald-400/15 to-cyan-400/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-gradient-to-br from-orange-400/10 to-pink-400/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 px-4 py-2 text-sm font-medium bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
              <Sparkles className="w-4 h-4 mr-2" />
              Nowa era zarządzania wspólnotami
            </Badge>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Rozliczenia wspólnot
              </span>
              <br />
              <span className="bg-gradient-to-r from-primary via-blue-600 to-violet-600 bg-clip-text text-transparent">
                bez stresu
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Automatyzuj rozliczenia czynszowe, zarządzaj licznikami i komunikuj się z mieszkańcami
              w jednym, intuicyjnym systemie.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/login">
                <Button size="lg" className="h-14 px-8 text-lg font-semibold shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all hover:scale-105">
                  <Zap className="w-5 h-5 mr-2" />
                  Rozpocznij teraz
                </Button>
              </Link>
              <a href="#funkcje">
                <Button size="lg" variant="outline" className="h-14 px-8 text-lg font-semibold border-2 hover:bg-gray-50">
                  Zobacz funkcje
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Prezentacja demo</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                <span>Bezpieczne dane</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-violet-500" />
                <span>Wsparcie techniczne</span>
              </div>
            </div>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none" />
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-2 shadow-2xl shadow-gray-900/20 max-w-5xl mx-auto">
              <div className="bg-gray-100 rounded-xl p-1">
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <div className="flex-1 mx-4">
                    <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-400 max-w-xs">
                      zamek.ai-dla-firmy.pl
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-lg p-8 min-h-[300px] flex items-center justify-center">
                  <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
                    <Card className="p-4 text-center shadow-lg">
                      <Building2 className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                      <div className="text-2xl font-bold text-gray-900">4</div>
                      <div className="text-xs text-gray-500">Wspólnoty</div>
                    </Card>
                    <Card className="p-4 text-center shadow-lg">
                      <Users className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                      <div className="text-2xl font-bold text-gray-900">122</div>
                      <div className="text-xs text-gray-500">Lokale</div>
                    </Card>
                    <Card className="p-4 text-center shadow-lg">
                      <Gauge className="w-8 h-8 mx-auto mb-2 text-violet-500" />
                      <div className="text-2xl font-bold text-gray-900">300+</div>
                      <div className="text-xs text-gray-500">Liczniki</div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                  {benefit.stat}
                </div>
                <div className="font-semibold text-gray-900 mb-1">{benefit.label}</div>
                <div className="text-sm text-gray-500">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funkcje" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 px-3 py-1 text-sm font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
              Funkcjonalności
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Wszystko czego potrzebujesz
            </h2>
            <p className="text-xl text-gray-600">
              Kompletny system do zarządzania rozliczeniami wspólnot mieszkaniowych.
              Od liczników po faktury - wszystko w jednym miejscu.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-100 hover:border-gray-200 overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 px-3 py-1 text-sm font-medium bg-violet-50 text-violet-700 border-violet-200">
              Jak to działa
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Rozliczenia w 4 krokach
            </h2>
            <p className="text-xl text-gray-600">
              Prosty i przejrzysty proces - od odczytów do wysyłki faktur.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowSteps.map((step, index) => (
              <div key={index} className="relative group">
                {index < workflowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-gray-200 to-transparent z-0" />
                )}
                <div className="relative z-10 text-center">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-white shadow-xl border border-gray-100 flex items-center justify-center group-hover:shadow-2xl group-hover:scale-105 transition-all">
                    <step.icon className="w-10 h-10 text-primary" />
                  </div>
                  <div className="text-sm font-bold text-primary mb-2">{step.step}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="cennik" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 px-3 py-1 text-sm font-medium bg-blue-50 text-blue-700 border-blue-200">
              Cennik
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Prosty i przejrzysty cennik
            </h2>
            <p className="text-xl text-gray-600">
              Bez ukrytych opłat. Płacisz tylko za to, czego używasz.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <Card className="relative overflow-hidden border-2 border-primary shadow-2xl shadow-primary/10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent" />
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <Badge className="mb-4 bg-primary text-white border-0">
                    Najpopularniejszy
                  </Badge>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Plan Professional</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-bold text-gray-900">10</span>
                    <span className="text-xl text-gray-500">zł brutto / miesiąc</span>
                  </div>
                  <p className="text-gray-500 mt-2">za każdy lokal</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pricingFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a href="mailto:kontakt@ai-dla-firmy.pl?subject=ZAMEK%20-%20Zamówienie%20demo" className="block">
                  <Button size="lg" className="w-full h-14 text-lg font-semibold shadow-lg shadow-primary/25">
                    Zamów prezentację demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Skontaktuj się z nami, a pokażemy Ci system w działaniu.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-primary via-blue-600 to-violet-600 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Gotowy na automatyzację rozliczeń?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Dołącz do zarządców, którzy już oszczędzają czas dzięki ZAMEK.
            Umów prezentację i przekonaj się sam.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:kontakt@ai-dla-firmy.pl?subject=ZAMEK%20-%20Prośba%20o%20demo">
              <Button size="lg" variant="secondary" className="h-14 px-8 text-lg font-semibold bg-white text-primary hover:bg-gray-100 shadow-xl">
                <Zap className="w-5 h-5 mr-2" />
                Umów demo
              </Button>
            </a>
            <Link to="/login">
              <Button size="lg" variant="outline" className="h-14 px-8 text-lg font-semibold border-2 border-white/30 text-white hover:bg-white/10">
                <Mail className="w-5 h-5 mr-2" />
                Zaloguj się
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">ZAMEK</span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-400">
              <a href="#funkcje" className="hover:text-white transition-colors">Funkcje</a>
              <a href="#cennik" className="hover:text-white transition-colors">Cennik</a>
              <Link to="/login" className="hover:text-white transition-colors">Zaloguj się</Link>
              <a href="mailto:kontakt@ai-dla-firmy.pl" className="hover:text-white transition-colors">Kontakt</a>
            </div>

            <div className="text-gray-500 text-sm">
              &copy; 2026 ZAMEK. Wszystkie prawa zastrzeżone.
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
